import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Pct, Spark } from "@/components/layernav/primitives";
import { fmtBig } from "./SectorHero";
import { ProtocolLogo } from "./ProtocolLogo";
import { entityNavRoute, entityTrack, resolveEntity, useEntityRegistry } from "@/lib/entities";
import { useEntitySync } from "./entitySync";

const upOf = (spark?: number[]) => !!spark && spark.length >= 2 && spark[spark.length - 1] >= spark[0];

/* 24H Protocol Movement Distribution — real positions on a centered axis,
   official medallions as plot markers. Same eligible universe as the payload. */
const MovementDistribution = ({ protocols }: { protocols: any[] }) => {
  const { data: reg } = useEntityRegistry();
  const { activeId, setActiveId } = useEntitySync();
  const pts = (protocols || [])
    .filter((p) => p.tvl_change_24h_pct != null)
    .map((p) => ({ ...p, v: p.tvl_change_24h_pct }))
    .sort((a, b) => a.v - b.v);
  if (pts.length < 4) return null;
  const maxAbs = Math.max(...pts.map((p) => Math.abs(p.v)));
  const range = Math.max(0.5, Math.ceil(maxAbs * 1.15 * 2) / 2);
  const pos = (v: number) => Math.min(97, Math.max(3, 50 + (v / range) * 47));
  // lane packing: nudge near-identical positions off-axis instead of overlapping
  const lanes: number[] = [-99, -99, -99];
  const placed = pts.map((p) => {
    const x = pos(p.v);
    let lane = lanes.findIndex((lx) => x - lx >= 7);
    if (lane === -1) lane = 0;
    lanes[lane] = x;
    return { ...p, x, lane };
  });
  const mid = pts.length / 2;
  const median = pts.length % 2 ? pts[Math.floor(mid)].v : (pts[mid - 1].v + pts[mid].v) / 2;
  const lo = pts[0], hi = pts[pts.length - 1];
  const laneTop = ["top-1/2 -translate-y-1/2", "top-0", "bottom-0"];
  return (
    <div className="hidden sm:block mt-auto pt-5" data-testid="wc-distribution">
      <div className="flex items-baseline justify-between">
        <p className="font-mono text-[8.5px] font-bold uppercase tracking-[0.2em] text-gray-400">
          24H Protocol Movement
        </p>
        <span className="font-mono text-[8px] uppercase tracking-wide text-gray-600">Distribution</span>
      </div>
      <div className="relative h-[74px] mt-2" role="img"
        aria-label={`24 hour TVL movement distribution across ${pts.length} protocols, from ${lo.protocol} ${lo.v}% to ${hi.protocol} +${hi.v}%`}>
        <span aria-hidden className="absolute inset-y-0 left-0 w-1/2 rounded-l"
          style={{ background: "linear-gradient(to left, transparent, rgba(239,68,68,.05))" }} />
        <span aria-hidden className="absolute inset-y-0 right-0 w-1/2 rounded-r"
          style={{ background: "linear-gradient(to right, transparent, rgba(0,229,255,.05))" }} />
        <span aria-hidden className="absolute left-0 right-0 top-1/2 h-px bg-white/[0.10]" />
        <span aria-hidden className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-4 w-px bg-white/[0.22]" />
        {placed.map((p) => {
          const route = entityNavRoute(resolveEntity(reg, "protocol", p.slug));
          const eid = `protocol:${p.slug}`;
          const isActive = activeId === eid;
          const sync = {
            onMouseEnter: () => setActiveId(eid), onMouseLeave: () => setActiveId(null),
            onFocus: () => setActiveId(eid), onBlur: () => setActiveId(null),
          };
          const cls = `absolute -translate-x-1/2 ${laneTop[p.lane]} rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]/70 hover:z-10 focus:z-10 transition-transform duration-200 hover:scale-110 ${isActive ? "scale-125 z-20 ring-2 ring-[#00E5FF]/50" : ""}`;
          const label = `${p.protocol}: ${fmtBig(p.tvl_usd)} TVL, ${p.v > 0 ? "up" : "down"} ${Math.abs(p.v)} percent over 24 hours`;
          const tip = `${p.protocol} · ${fmtBig(p.tvl_usd)} TVL · ${p.v > 0 ? "+" : ""}${p.v}% 24H${p.tvl_change_7d_pct != null ? ` · ${p.tvl_change_7d_pct > 0 ? "+" : ""}${p.tvl_change_7d_pct}% 7D` : ""}`;
          return route ? (
            <Link key={p.slug} to={route} data-testid={`wc-dist-marker-${p.slug}`} data-live="true"
              title={tip} aria-label={`${label}. View entity page.`} {...sync}
              onClick={() => entityTrack("entity_nav_click", `protocol:${p.slug}`, { surface: "wc_distribution" })}
              className={cls} style={{ left: `${p.x}%` }}>
              <ProtocolLogo slug={p.slug} name={p.protocol} size={24} />
            </Link>
          ) : (
            <span key={p.slug} tabIndex={0} data-testid={`wc-dist-marker-${p.slug}`}
              title={tip} aria-label={label} {...sync}
              className={cls} style={{ left: `${p.x}%` }}>
              <ProtocolLogo slug={p.slug} name={p.protocol} size={24} />
            </span>
          );
        })}
      </div>
      <div className="flex justify-between font-mono text-[8px] uppercase tracking-wide text-gray-500 mt-1.5">
        <span className="text-[#EF4444]/80">← Decliners · −{range}%</span>
        <span className="text-gray-600">0</span>
        <span className="text-[#00E5FF]/80">+{range}% · Advancers →</span>
      </div>
      <p data-testid="wc-dist-summary" className="font-mono text-[8.5px] uppercase tracking-wide text-gray-500 mt-2">
        Median {median >= 0 ? "+" : ""}{median.toFixed(2)}% · Range {lo.v > 0 ? "+" : ""}{lo.v}% ↔ {hi.v > 0 ? "+" : ""}{hi.v}%
      </p>
    </div>
  );
};

// Significance thresholds: 24H TVL ±1.0%, 7D TVL ±3.0%
export const WhatChangedProtocols = ({ protocols }: { protocols: any[] }) => {
  const { data: reg } = useEntityRegistry();
  const { activeId, setActiveId } = useEntitySync();
  const rows = protocols || [];
  if (!rows.length) return null;
  const withDelta = rows.filter((p) => p.tvl_change_24h_pct != null);
  const adv = withDelta.filter((p) => p.tvl_change_24h_pct > 0).length;
  const dec = withDelta.filter((p) => p.tvl_change_24h_pct < 0).length;
  const gainer = withDelta.length ? withDelta.reduce((a, b) => (b.tvl_change_24h_pct > a.tvl_change_24h_pct ? b : a)) : null;
  const decliner = withDelta.length ? withDelta.reduce((a, b) => (b.tvl_change_24h_pct < a.tvl_change_24h_pct ? b : a)) : null;
  const moves: any[] = withDelta
    .filter((p) => Math.abs(p.tvl_change_24h_pct) >= 1.0)
    .map((p) => ({ ...p, tf: "24H", v: p.tvl_change_24h_pct }));
  const have = new Set(moves.map((m) => m.slug));
  for (const p of rows) {
    if (have.has(p.slug) || p.tvl_change_7d_pct == null || Math.abs(p.tvl_change_7d_pct) < 3.0) continue;
    moves.push({ ...p, tf: "7D", v: p.tvl_change_7d_pct });
  }
  moves.sort((a, b) => Math.abs(b.v) - Math.abs(a.v));
  const top = moves.slice(0, 5);
  return (
    <div data-testid="defi-what-changed"
      className="flex flex-col rounded-xl border border-white/[0.08] bg-[#0b0c10] p-4 sm:p-5">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#A78BFA]">What Changed</p>
        <span className="font-mono text-[9px] uppercase tracking-widest text-gray-500">24H</span>
      </div>
      {withDelta.length > 0 && (
        <p data-testid="wc-breadth" className="font-mono text-[9.5px] uppercase tracking-wide text-gray-400 mt-1.5">
          <span className="text-[#22C55E]">{adv} advancer{adv === 1 ? "" : "s"}</span>
          <span className="text-gray-600"> · </span>
          <span className="text-[#EF4444]">{dec} decliner{dec === 1 ? "" : "s"}</span>
          <span className="text-gray-600"> · top protocols</span>
        </p>
      )}
      {top.length ? (
        <div className="mt-2 divide-y divide-white/[0.05]">
          {top.map((m) => {
            const route = entityNavRoute(resolveEntity(reg, "protocol", m.slug));
            const eid = `protocol:${m.slug}`;
            const isActive = activeId === eid;
            const detail = (
              <>
                <span className="block font-mono text-[8.5px] uppercase tracking-wide text-gray-500 truncate mt-0.5">
                  {m.category}{m.dominant_chain ? ` · ${m.dominant_chain}` : ""}
                </span>
                <span className="flex items-baseline gap-2 mt-1">
                  <span className="font-mono text-[12px] font-semibold text-white">{fmtBig(m.tvl_usd)}</span>
                  <Pct v={m.v} className="!text-[12px] !font-bold" />
                  {m.tf === "7D" && <span className="font-mono text-[8px] uppercase text-gray-500">7D</span>}
                </span>
              </>
            );
            return (
              <div key={`${m.slug}-${m.tf}`} data-testid={`wc-protocol-${m.slug}`} data-live={route ? "true" : "false"}
                data-sync-active={isActive ? "true" : undefined}
                onMouseEnter={() => setActiveId(eid)} onMouseLeave={() => setActiveId(null)}
                className={`${route ? "group relative" : ""} grid grid-cols-[minmax(0,1fr)_92px] items-center gap-3 px-2 -mx-2 rounded-lg transition-colors duration-200 ${isActive ? "bg-white/[0.06] ring-1 ring-[#00E5FF]/25" : "hover:bg-white/[0.04]"} ${top.length < 5 ? "py-4" : "py-2.5"}`}>
                <span className="flex items-start gap-2.5 min-w-0">
                  <ProtocolLogo slug={m.slug} name={m.protocol} size={top.length < 5 ? 40 : 34}
                    className={route ? "transition-transform duration-200 group-hover:scale-[1.05]" : ""} />
                  {route ? (
                    <Link to={route} aria-label={`${m.protocol} — Entity Intelligence`}
                      data-testid={`entity-link-protocol-${m.slug}`}
                      onFocus={() => setActiveId(eid)} onBlur={() => setActiveId(null)}
                      onClick={() => entityTrack("entity_nav_click", `protocol:${m.slug}`, { surface: "wc_protocols" })}
                      className="min-w-0 focus-visible:outline-none after:absolute after:inset-0 after:rounded-lg focus-visible:after:ring-1 focus-visible:after:ring-[#00E5FF]/60">
                      <span className="flex items-center gap-1 min-w-0">
                        <span className={`font-bold text-[13px] leading-tight truncate transition-colors duration-200 ${isActive ? "text-[#00E5FF]" : "text-white group-hover:text-[#00E5FF]"}`}>{m.protocol}</span>
                        <ArrowUpRight aria-hidden
                          className={`h-3 w-3 shrink-0 text-[#00E5FF] transition-opacity duration-200 ${isActive ? "opacity-100" : "opacity-50 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"}`} />
                      </span>
                      {detail}
                    </Link>
                  ) : (
                    <span className="min-w-0">
                      <span className="block font-bold text-[13px] text-white leading-tight truncate">{m.protocol}</span>
                      {detail}
                    </span>
                  )}
                </span>
                <span title="7-day TVL trend · NewsLayer series">
                  {m.spark?.length >= 2
                    ? <Spark points={m.spark} up={upOf(m.spark)} className={`w-[92px] ${top.length < 5 ? "h-12" : "h-8"}`} />
                    : <span className="font-mono text-[9px] text-gray-600">building…</span>}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p data-testid="wc-protocols-empty" className="text-[12.5px] text-gray-500 leading-relaxed mt-3">
          No significant protocol TVL movement in the last 24H.
        </p>
      )}
      {top.length < 4 && gainer && decliner && (
        <div className="grid grid-cols-2 gap-2 mt-4" data-testid="wc-callouts">
          {[{ p: gainer, tone: "#22C55E", label: "Top Gainer" }, { p: decliner, tone: "#EF4444", label: "Top Decliner" }].map(({ p, tone, label }) => {
            const route = entityNavRoute(resolveEntity(reg, "protocol", p.slug));
            const identity = (
              <>
                <ProtocolLogo slug={p.slug} name={p.protocol} size={20} />
                <span className={`font-bold text-[12px] text-white truncate ${route ? "transition-colors duration-200 group-hover:text-[#00E5FF]" : ""}`}>{p.protocol}</span>
              </>
            );
            return (
              <div key={label} className="rounded-lg border border-white/[0.07] bg-white/[0.02] px-3 py-3.5">
                <p className="font-mono text-[8px] font-bold uppercase tracking-[0.18em]" style={{ color: tone }}>{label}</p>
                <p className="flex items-center gap-1.5 mt-1.5 min-w-0">
                  {route ? (
                    <Link to={route} aria-label={`${p.protocol} — Entity Intelligence`}
                      data-testid={`entity-link-protocol-${p.slug}`}
                      onClick={() => entityTrack("entity_nav_click", `protocol:${p.slug}`, { surface: "wc_callouts" })}
                      className="group inline-flex items-center gap-1.5 min-w-0 rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00E5FF]/60">
                      {identity}
                    </Link>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 min-w-0">{identity}</span>
                  )}
                  <Pct v={p.tvl_change_24h_pct} className="!text-[11px]" />
                </p>
              </div>
            );
          })}
        </div>
      )}
      <MovementDistribution protocols={rows} />
      <div className="pt-3 flex items-center justify-between gap-3">
        <p className="font-mono text-[8.5px] uppercase tracking-wider text-gray-500">
          Significance ≥1% 24H · ≥3% 7D · DeFiLlama
        </p>
        <Link to="/defi/protocols" data-testid="wc-view-all"
          className="group font-mono text-[10px] font-bold text-[#34D399] hover:text-white transition-colors whitespace-nowrap">
          View all <ArrowRight className="inline h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" />
        </Link>
      </div>
    </div>
  );
};

const ROW = "grid grid-cols-[minmax(0,1.5fr)_70px_52px_60px] sm:grid-cols-[minmax(0,1.5fr)_78px_54px_54px_84px_58px] items-center gap-x-2";

export const ProtocolMovers = ({ protocols }: { protocols: any[] }) => {
  const { data: reg } = useEntityRegistry();
  const ranked = (protocols || [])
    .filter((p) => p.tvl_change_24h_pct != null)
    .sort((a, b) => Math.abs(b.tvl_change_24h_pct) - Math.abs(a.tvl_change_24h_pct))
    .slice(0, 5);
  if (ranked.length < 3) return null;
  return (
    <div data-testid="defi-protocol-movers"
      className="h-full flex flex-col rounded-xl border border-white/[0.08] bg-[#0b0c10] p-4 sm:p-5">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#34D399]">Protocol Movers</p>
        <span className="font-mono text-[8.5px] uppercase tracking-wider text-gray-500">Strongest 24H TVL moves</span>
      </div>
      <div className={`${ROW} font-mono text-[9px] uppercase tracking-wider text-gray-500 mt-3 mb-1`}>
        <span>Protocol</span><span className="text-right">TVL</span><span className="text-right">24H</span>
        <span className="text-right hidden sm:block">7D</span>
        <span className="text-center hidden sm:block">Trend · 7D</span>
        <span className="text-right"><span className="sm:hidden">Trend</span><span className="hidden sm:inline">Token</span></span>
      </div>
      <div className="divide-y divide-white/[0.04]">
        {ranked.map((p) => {
          const route = entityNavRoute(resolveEntity(reg, "protocol", p.slug));
          return (
          <div key={p.slug} data-testid={`mover-row-${p.slug}`} data-live={route ? "true" : "false"}
            className={`${ROW} ${route ? "group relative" : ""} px-2 -mx-2 py-2.5 rounded-lg hover:bg-white/[0.04] transition-colors`}>
            <span className="flex items-center gap-2 min-w-0">
              <ProtocolLogo slug={p.slug} name={p.protocol} size={26}
                className={route ? "transition-transform duration-200 group-hover:scale-[1.05]" : ""} />
              {route ? (
                <Link to={route} aria-label={`${p.protocol} — Entity Intelligence`}
                  data-testid={`entity-link-protocol-${p.slug}`}
                  onClick={() => entityTrack("entity_nav_click", `protocol:${p.slug}`, { surface: "protocol_movers" })}
                  className="min-w-0 focus-visible:outline-none after:absolute after:inset-0 after:rounded-lg focus-visible:after:ring-1 focus-visible:after:ring-[#00E5FF]/60">
                  <span className="flex items-center gap-1 min-w-0">
                    <span className="font-bold text-[13px] text-white leading-tight truncate transition-colors duration-200 group-hover:text-[#00E5FF]">{p.protocol}</span>
                    <ArrowUpRight aria-hidden
                      className="h-3 w-3 shrink-0 text-[#00E5FF] opacity-50 md:opacity-0 transition-opacity duration-200 md:group-hover:opacity-100 md:group-focus-within:opacity-100" />
                  </span>
                  <span className="block font-mono text-[9px] uppercase tracking-wide text-gray-400 truncate">
                    {p.category}{p.dominant_chain ? ` · ${p.dominant_chain}` : ""}
                  </span>
                </Link>
              ) : (
                <span className="min-w-0">
                  <span className="block font-bold text-[13px] text-white leading-tight truncate">{p.protocol}</span>
                  <span className="block font-mono text-[9px] uppercase tracking-wide text-gray-400 truncate">
                    {p.category}{p.dominant_chain ? ` · ${p.dominant_chain}` : ""}
                  </span>
                </span>
              )}
            </span>
            <span className="font-mono text-[12.5px] font-semibold text-white text-right">{fmtBig(p.tvl_usd)}</span>
            <span className="text-right"><Pct v={p.tvl_change_24h_pct} className="!text-[11px]" /></span>
            <span className="text-right hidden sm:block">
              {p.tvl_change_7d_pct != null ? <Pct v={p.tvl_change_7d_pct} className="!text-[11px]" />
                : <span className="font-mono text-[10px] text-gray-600">—</span>}
            </span>
            <span className="hidden sm:flex justify-center" title="7-day TVL trend · NewsLayer series">
              {p.spark?.length >= 2
                ? <Spark points={p.spark} up={upOf(p.spark)} className="w-[72px] h-5" />
                : <span className="font-mono text-[9px] text-gray-600">building…</span>}
            </span>
            <span className="text-right">
              <span className="sm:hidden">
                {p.spark?.length >= 2
                  ? <Spark points={p.spark} up={upOf(p.spark)} className="w-[56px] h-5" />
                  : <span className="font-mono text-[9px] text-gray-600">—</span>}
              </span>
              <span className="hidden sm:block">
                {p.token ? (
                  p.token.has_coin_page ? (
                    <Link to={`/coin/${p.token.symbol}`} data-testid={`mover-token-${p.token.symbol}`}
                      onClick={(e) => e.stopPropagation()}
                      className="relative z-10 flex items-center justify-end gap-1.5 rounded hover:bg-white/[0.06] px-1 -mx-1 py-0.5 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#A78BFA]/60">
                      <span className="font-mono text-[10px] font-bold text-[#A78BFA]">{p.token.symbol}</span>
                      <Pct v={p.token.change_24h_pct} className="!text-[10.5px]" />
                    </Link>
                  ) : (
                    <span className="flex items-center justify-end gap-1.5">
                      <span className="font-mono text-[10px] font-bold text-[#A78BFA]">{p.token.symbol}</span>
                      <Pct v={p.token.change_24h_pct} className="!text-[10.5px]" />
                    </span>
                  )
                ) : <span className="font-mono text-[10px] text-gray-600">—</span>}
              </span>
            </span>
          </div>
          );
        })}
      </div>
      <p className="mt-auto pt-3 font-mono text-[8.5px] uppercase tracking-wider text-gray-500">
        Ranked by 24H TVL move · DeFiLlama · 7D trend from NewsLayer series · Tokens via Kraken
      </p>
    </div>
  );
};
